import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export default function useHotelHandlers(estateId, isResident, isLocked) {
  const { t } = useTranslation("hotel");

  const [isResidentPending, setIsResidentPending] = useState(false);
  const [isSelling, setIsSelling] = useState(false);

  const onCloseControl = useCallback(() => {
    GameMpProvider.closeCurrentInterface(true);
  }, []);

  const setResident = useCallback(
    async (onConfirm?: () => void) => {
      if (isResident) {
        toast.error(t(`hotel.errors.SAME_ENTITY_ERROR`));
        return;
      }

      if (estateId) {
        setIsResidentPending(true);

        await EstateMpProvider.setResident(estateId)
          .then((response) => {
            if (response instanceof RpcMpProviderSuccessResponse) {
              onConfirm?.();
              toast.success(t("hotel.toast.residentSuccess"));
            } else {
              console.error(response);
              toast.error(t("hotel.toast.residentError"));
            }
          })
          .catch((e) => {
            console.error(e);
            toast.error(t("hotel.toast.residentSystemError"));
          })
          .finally(() => {
            setIsResidentPending(false);
          });
      }
    },
    [estateId, isResident, t],
  );

  const toggleLockHotel = useCallback(
    async (successCallback: () => void) => {
      if (estateId) {
        const response = await EstateMpProvider.lock(estateId, !isLocked);
        if (response instanceof RpcMpProviderSuccessResponse) {
          successCallback();
        } else if (response.message === "TOO_FAR_ERROR") {
          toast.error(t("hotel.errors.TOO_FAR_ERROR"));
        } else {
          toast.error(t("hotel.toast.lockError"));
        }
      }
    },
    [estateId, isLocked, t],
  );

  const sell = useCallback(async () => {
    if (estateId) {
      setIsSelling(true);

      await EstateMpProvider.sell(estateId)
        .then((response) => {
          if (response instanceof RpcMpProviderSuccessResponse) {
            toast.success(t("hotel.toast.sellSuccess"));
            onCloseControl();
          } else {
            console.error(response);
            toast.error(t("hotel.toast.sellError"));
          }
        })
        .catch((e) => {
          console.error(e);
          toast.error(t("hotel.toast.sellSystemError"));
        })
        .finally(() => {
          setIsSelling(false);
        });
    }
  }, [estateId, onCloseControl, t]);

  return {
    isResidentPending,
    setResident,
    toggleLockHotel,
    onCloseControl,
    isSelling,
    sell,
  };
}
